import * as THREE from 'three'
import { Heightfield } from '../terrain/heightfield'
import type { Pose } from '../physics/skier'
import { clamp01, damp, lerp, smoothstep } from '../core/math'

/**
 * The chase camera. Spec §8, and spec §12 risk #1: camera readability is the
 * biggest risk in the whole design, so every bullet in §8 is implemented here
 * rather than approximated.
 *
 *   - elevated chase cam, 4-6m back, 2.5-3m up, pitched down
 *   - speed-reactive FOV and distance
 *   - look-ahead bias, leading steering input by ~0.2s
 *   - crest anticipation on convex rollovers
 *
 * The pitch-down falls out of the aim point rather than being dialled in: the
 * camera looks at the *terrain surface* some distance ahead, so it pitches down
 * automatically on a 45° face and levels out on the apron.
 */

const CAM = {
  DIST_SLOW: 4.6,
  DIST_FAST: 6.0,
  HEIGHT_SLOW: 2.6,
  HEIGHT_FAST: 3.1,
  FOV_SLOW: 62,
  FOV_FAST: 82,
  /** Speed, m/s, at which the "fast" values are fully reached. */
  SPEED_REF: 30,

  /** How far ahead the aim point sits, at rest and at speed. */
  LOOK_AHEAD_SLOW: 16,
  LOOK_AHEAD_FAST: 34,
  /** Aim point sits this far above the terrain it is sampling. */
  LOOK_LIFT: 2.2,

  /** Radians of yaw lead at full edge — roughly 0.2s of turn at full lock. */
  STEER_LEAD: 0.23,

  /** Extra height at a fully detected crest. */
  CREST_LIFT: 5.2,
  /** Extra look-ahead at a fully detected crest. */
  CREST_LOOK: 14,
  /** Convexity, in metres of chord deviation, that reads as a full crest. */
  CREST_FULL: 4.0,

  /** Minimum clearance the camera keeps above the snow. */
  CLEARANCE: 1.7,

  POS_DAMP: 7.5,
  POS_DAMP_AIR: 4.0,
  LOOK_DAMP: 6.0,
  FOV_DAMP: 3.5,

  /**
   * Camera shake. Amplitude in metres at full speed, on rough ground.
   *
   * A perfectly smooth camera reads as a camera on rails, and at 25 m/s over
   * wind crust it is the single most obvious thing missing. Scaled by both
   * speed and the roughness of the surface under the skis, so it is quiet on a
   * groomed-feeling apron and violent through a choke — it reports what the
   * snow is doing rather than just decorating the frame.
   */
  SHAKE_AMPLITUDE: 0.16,
  SHAKE_SPEED_REF: 26,
  /** Shake frequencies, deliberately incommensurate so it never loops audibly. */
  SHAKE_HZ: [17.3, 23.9, 11.7],
} as const

export class ChaseCamera {
  readonly camera: THREE.PerspectiveCamera
  private readonly pos = new THREE.Vector3()
  private readonly look = new THREE.Vector3()
  private readonly desiredPos = new THREE.Vector3()
  private readonly desiredLook = new THREE.Vector3()
  private fov: number = CAM.FOV_SLOW
  private crest = 0
  private shakeTime = 0
  private initialised = false

  constructor(private readonly hf: Heightfield, aspect: number) {
    // Far plane clears the outermost backdrop ring at 4km.
    this.camera = new THREE.PerspectiveCamera(CAM.FOV_SLOW, aspect, 0.5, 8000)
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect
    this.camera.updateProjectionMatrix()
  }

  /** Snap immediately, e.g. on reset or teleport. */
  snap(): void {
    this.initialised = false
  }

  update(pose: Pose, speed: number, edge: number, airborne: boolean, dt: number): void {
    const speedT = clamp01(speed / CAM.SPEED_REF)

    // Look-ahead bias: aim where the steering is about to take us, not where
    // the skis are pointed right now.
    const yaw = pose.heading - edge * CAM.STEER_LEAD
    const fx = Math.sin(yaw)
    const fz = Math.cos(yaw)

    // --- Crest anticipation ------------------------------------------------
    // Chord test along the direction of travel: if the ground 18m ahead sits
    // above the straight line from here to 45m ahead, we are on a rollover and
    // cannot see what is below it.
    const h0 = this.hf.sampleHeight(pose.x, pose.z)
    const hMid = this.hf.sampleHeight(pose.x + fx * 18, pose.z + fz * 18)
    const hFar = this.hf.sampleHeight(pose.x + fx * 45, pose.z + fz * 45)
    const convexity = hMid - (h0 + hFar) * 0.5
    const crestTarget = smoothstep(0.4, CAM.CREST_FULL, convexity)
    // Rises fast (you need the sightline now) and falls slowly (no snapping
    // back down the instant you clear the lip).
    this.crest = damp(this.crest, crestTarget, crestTarget > this.crest ? 9 : 2.2, dt)

    const dist = lerp(CAM.DIST_SLOW, CAM.DIST_FAST, speedT)
    const height = lerp(CAM.HEIGHT_SLOW, CAM.HEIGHT_FAST, speedT) + this.crest * CAM.CREST_LIFT

    this.desiredPos.set(pose.x - fx * dist, pose.y + height, pose.z - fz * dist)

    // Never clip through the mountain, and never sit so low that the skier
    // occludes the ground they are about to ski.
    const groundAtCam = this.hf.sampleHeight(this.desiredPos.x, this.desiredPos.z)
    if (this.desiredPos.y < groundAtCam + CAM.CLEARANCE) {
      this.desiredPos.y = groundAtCam + CAM.CLEARANCE
    }

    // --- Aim point ---------------------------------------------------------
    const ahead = lerp(CAM.LOOK_AHEAD_SLOW, CAM.LOOK_AHEAD_FAST, speedT) + this.crest * CAM.CREST_LOOK
    const ax = pose.x + fx * ahead
    const az = pose.z + fz * ahead
    const ay = this.hf.sampleHeight(ax, az) + CAM.LOOK_LIFT
    this.desiredLook.set(ax, ay, az)

    if (!this.initialised) {
      this.pos.copy(this.desiredPos)
      this.look.copy(this.desiredLook)
      this.fov = lerp(CAM.FOV_SLOW, CAM.FOV_FAST, speedT)
      this.initialised = true
    } else {
      // Looser in the air so a drop doesn't yank the frame.
      const rate = airborne ? CAM.POS_DAMP_AIR : CAM.POS_DAMP
      const k = 1 - Math.exp(-rate * dt)
      this.pos.lerp(this.desiredPos, k)
      this.look.lerp(this.desiredLook, 1 - Math.exp(-CAM.LOOK_DAMP * dt))
      this.fov = damp(this.fov, lerp(CAM.FOV_SLOW, CAM.FOV_FAST, speedT), CAM.FOV_DAMP, dt)
    }

    // Hard floor after smoothing too — damping can dip the camera into a lip.
    const g = this.hf.sampleHeight(this.pos.x, this.pos.z)
    if (this.pos.y < g + CAM.CLEARANCE * 0.8) this.pos.y = g + CAM.CLEARANCE * 0.8

    // --- Shake -------------------------------------------------------------
    // Driven off the terrain's own roughness under the skier, so chatter comes
    // from the ground rather than from a timer. Suppressed in the air, where
    // there is nothing to be shaken by.
    this.shakeTime += dt
    const rough = airborne ? 0 : clamp01(Math.abs(this.hf.sampleConcavity(pose.x, pose.z)) * 22)
    const shake = CAM.SHAKE_AMPLITUDE * speedT * (0.3 + rough * 0.7)
    if (shake > 0.001) {
      const t = this.shakeTime
      this.camera.position.set(
        this.pos.x + Math.sin(t * CAM.SHAKE_HZ[0]) * shake,
        this.pos.y + Math.sin(t * CAM.SHAKE_HZ[1]) * shake * 1.3,
        this.pos.z + Math.sin(t * CAM.SHAKE_HZ[2]) * shake * 0.6,
      )
    } else {
      this.camera.position.copy(this.pos)
    }
    this.camera.lookAt(this.look)
    if (Math.abs(this.camera.fov - this.fov) > 0.01) {
      this.camera.fov = this.fov
      this.camera.updateProjectionMatrix()
    }
  }

  /** Debug readout of the crest detector. */
  get crestAmount(): number {
    return this.crest
  }
}
